/**
 * Menu "Filtros técnicos" — Equipe2 (Coordenador, Área, Técnico, Cargo + Especialidade técnica).
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { IconCertificate, IconStack2 } from '@tabler/icons-react';
import { ChevronDown, Plus } from 'lucide-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import { EC_FILTER_POPOVER_CLASS } from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import { clearEquipe2TechFilters, countEquipe2TechFilters } from '../utils/equipe2TechFilterHelpers';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../experiencia-cliente/components/ExperienciaClientePage.css';

const TOP_FILTER_DEFS = [
  { key: 'coordenador', label: 'Coordenador', listAriaLabel: 'Coordenadores' },
  { key: 'area', label: 'Área', listAriaLabel: 'Áreas' },
  { key: 'tecnico', label: 'Técnico', listAriaLabel: 'Técnicos' },
  { key: 'cargo', label: 'Cargo', listAriaLabel: 'Cargos' },
];

const ESP_FILTER_DEFS = [
  { key: 'fabricante', label: 'Fabricante', listAriaLabel: 'Fabricantes' },
  { key: 'tecnologia', label: 'Tecnologia', listAriaLabel: 'Tecnologias' },
];

const STATE_KEYS = {
  coordenador: 'coordenadores',
  area: 'areas',
  tecnico: 'tecnicos',
  cargo: 'cargos',
  fabricante: 'fabricantes',
  tecnologia: 'tecnologias',
};

const STATIC_KEYS = new Set(['cargo', 'fabricante', 'tecnologia']);

const ALL_FILTER_DEFS = [...TOP_FILTER_DEFS, ...ESP_FILTER_DEFS];

function FilterChip({ chip, isActive, chipRefs, onSelectFilter, count }) {
  return (
    <div
      className="board-search-filters-chip-anchor"
      ref={(el) => {
        if (el) chipRefs.current[chip.key] = el;
        else delete chipRefs.current[chip.key];
      }}
    >
      <button
        type="button"
        className={[
          'board-search-filters-chip',
          isActive ? 'is-active' : '',
          count > 0 ? 'has-selection' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Filtrar por ${chip.label}`}
        aria-expanded={isActive}
        onClick={() => onSelectFilter(chip.key)}
      >
        <Plus
          size={11}
          strokeWidth={1.75}
          className="board-search-filters-chip-icon board-search-filters-chip-icon--plus"
          aria-hidden
        />
        <span className="board-search-filters-chip-label">{chip.label}</span>
        {count > 0 ? (
          <span className="board-search-filters-chip-count">{count}</span>
        ) : null}
        <ChevronDown
          size={11}
          strokeWidth={1.75}
          className={[
            'board-search-filters-chip-icon',
            'board-search-filters-chip-icon--chevron',
            isActive ? 'is-open' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden
        />
      </button>
    </div>
  );
}

function buildPopoverStyle(anchorRect) {
  if (!anchorRect) return null;
  const width = 238;
  const maxHeight = 320;
  const margin = 8;
  const spaceBelow = window.innerHeight - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const flipped = spaceBelow < 220 && spaceAbove > spaceBelow;
  const available = flipped ? spaceAbove : spaceBelow;
  const height = Math.min(maxHeight, Math.max(160, available));
  const top = flipped ? anchorRect.top - height - 4 : anchorRect.bottom + 4;
  let left = anchorRect.left;
  if (left + width > window.innerWidth - margin) {
    left = window.innerWidth - width - margin;
  }
  left = Math.max(margin, left);
  return {
    position: 'fixed',
    top,
    left,
    width,
    boxSizing: 'border-box',
    zIndex: 10050,
    maxHeight: height,
    '--bsf-cidade-popover-max-h': `${height}px`,
  };
}

function AsyncFilterPopover({
  fieldKey,
  listAriaLabel,
  selected,
  anchorRect,
  onToggle,
  onToggleAll,
  onFetchSuggestions,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!anchorRect) return undefined;
    let cancelled = false;
    setLoading(true);
    onFetchSuggestions(fieldKey, searchTerm)
      .then((list) => {
        if (!cancelled) setItems(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [fieldKey, searchTerm, onFetchSuggestions, anchorRect]);

  const listItems = useMemo(
    () => items.map((name) => ({ value: name, label: name })),
    [items],
  );

  const popoverStyle = useMemo(() => buildPopoverStyle(anchorRect), [anchorRect]);
  if (!anchorRect || !popoverStyle) return null;

  return ReactDOM.createPortal(
    <div
      data-eq2-tech-filter-popover={fieldKey}
      className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--${fieldKey}`}
      style={popoverStyle}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        searchPlaceholder="Buscar"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        items={listItems}
        getKey={(item) => item.value}
        getLabel={(item) => item.label}
        selectedKeys={selected}
        onToggle={(item) => onToggle(item.value)}
        emptyMessage={loading ? 'Carregando…' : 'Nenhum resultado'}
        listAriaLabel={listAriaLabel}
        bulkMenuEnabled
        onBulkToggleAll={
          listItems.length > 0
            ? () => {
                const keys = listItems.map((i) => i.value);
                const allMarked = keys.length > 0 && keys.every((k) => selected.includes(k));
                onToggleAll(keys, allMarked);
              }
            : undefined
        }
      />
    </div>,
    document.body,
  );
}

function StaticFilterPopover({
  fieldKey,
  listAriaLabel,
  selected,
  anchorRect,
  items,
  onToggle,
  onToggleAll,
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const listItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [items, searchTerm]);

  const popoverStyle = useMemo(() => buildPopoverStyle(anchorRect), [anchorRect]);
  if (!anchorRect || !popoverStyle) return null;

  return ReactDOM.createPortal(
    <div
      data-eq2-tech-filter-popover={fieldKey}
      className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade`}
      style={popoverStyle}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        searchPlaceholder={`Buscar ${listAriaLabel.toLowerCase()}...`}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        items={listItems}
        getKey={(item) => item.value}
        getLabel={(item) => item.label}
        selectedKeys={selected}
        onToggle={(item) => onToggle(item.value)}
        emptyMessage="Nenhum resultado"
        listAriaLabel={listAriaLabel}
        bulkMenuEnabled
        onBulkToggleAll={
          listItems.length > 0
            ? () => {
                const keys = listItems.map((i) => i.value);
                const allMarked = keys.length > 0 && keys.every((k) => selected.includes(k));
                onToggleAll(keys, allMarked);
              }
            : undefined
        }
      />
    </div>,
    document.body,
  );
}

export function Equipe2TechFilters({
  filters,
  onChange,
  onFetchSuggestions,
  filterContext = {},
}) {
  const anchorRef = useRef(null);
  const chipRefs = useRef({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFilterKey, setActiveFilterKey] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [floatingStyle, setFloatingStyle] = useState({ top: 0, left: 0 });

  const activeCount = countEquipe2TechFilters(filters) + (filters.showInactive ? 1 : 0);

  const staticItemsByKey = useMemo(() => ({
    cargo: filterContext.cargoOptions || [],
    fabricante: filterContext.fabricanteOptions || [],
    tecnologia: filterContext.tecnologiaOptions || [],
  }), [filterContext]);

  const updateFloatingPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = Math.min(420, window.innerWidth - 32);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 6;
    setFloatingStyle({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) return;
    updateFloatingPosition();
  }, [menuOpen, activeFilterKey, updateFloatingPosition]);

  useLayoutEffect(() => {
    if (!activeFilterKey) {
      setAnchorRect(null);
      return;
    }
    const el = chipRefs.current[activeFilterKey];
    setAnchorRect(el ? el.getBoundingClientRect() : null);
  }, [activeFilterKey, menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onResize = () => updateFloatingPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [menuOpen, updateFloatingPosition]);

  useEffect(() => {
    if (!menuOpen && !activeFilterKey) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (anchorRef.current?.contains(t)) return;
      if (t.closest?.('[data-eq2-tech-filter-popover]')) return;
      if (t.closest?.('.board-search-filters-floating')) {
        if (activeFilterKey) {
          setActiveFilterKey(null);
          return;
        }
        return;
      }
      if (activeFilterKey && chipRefs.current[activeFilterKey]?.contains(t)) return;
      if (activeFilterKey) {
        setActiveFilterKey(null);
        return;
      }
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [menuOpen, activeFilterKey]);

  const handleSelectFilter = (key) => {
    if (key === 'clearAll') {
      onChange(clearEquipe2TechFilters());
      setActiveFilterKey(null);
      return;
    }
    setActiveFilterKey((prev) => (prev === key ? null : key));
  };

  const handleToggle = useCallback(
    (fieldKey, value) => {
      const stateKey = STATE_KEYS[fieldKey];
      const current = filters[stateKey] || [];
      const next = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      onChange({ ...filters, [stateKey]: next });
    },
    [filters, onChange],
  );

  const handleToggleAll = useCallback(
    (fieldKey, keys, allMarked) => {
      const stateKey = STATE_KEYS[fieldKey];
      const current = filters[stateKey] || [];
      const next = allMarked
        ? current.filter((x) => !keys.includes(x))
        : Array.from(new Set([...current, ...keys]));
      onChange({ ...filters, [stateKey]: next });
    },
    [filters, onChange],
  );

  const activeDef = ALL_FILTER_DEFS.find((d) => d.key === activeFilterKey);

  const floating = menuOpen && ReactDOM.createPortal(
    <>
      <div className="board-search-filters-backdrop" aria-hidden />
      <div
        className="board-search-filters-floating ec-tech-filters-floating"
        style={{ top: floatingStyle.top, left: floatingStyle.left }}
      >
        <div className="board-search-filters-menu ec-tech-filters-menu" role="dialog" aria-label="Filtros técnicos">
          <div className="board-search-filters-menu-header">
            <span className="board-search-filters-menu-title">Filtros técnicos</span>
            <button
              type="button"
              className="board-search-filters-menu-close"
              onClick={() => {
                setMenuOpen(false);
                setActiveFilterKey(null);
              }}
              aria-label="Fechar filtros técnicos"
            >
              ×
            </button>
          </div>
          <div className="board-search-filters-chips ec-tech-filters-chips" role="group" aria-label="Filtros técnicos">
            <div className="board-search-filters-chips-top">
              {activeCount > 0 ? (
                <button
                  type="button"
                  className="board-search-filters-chip board-search-filters-chip--clear"
                  onClick={() => handleSelectFilter('clearAll')}
                >
                  Limpar filtros
                </button>
              ) : null}
              {TOP_FILTER_DEFS.map((chip) => (
                <FilterChip
                  key={chip.key}
                  chip={chip}
                  isActive={activeFilterKey === chip.key}
                  chipRefs={chipRefs}
                  onSelectFilter={handleSelectFilter}
                  count={(filters[STATE_KEYS[chip.key]] || []).length}
                />
              ))}
            </div>
            <div className="bsf-chip-section">
              <div className="bsf-chip-section-title">
                <IconCertificate size={12} stroke={1.5} aria-hidden />
                <span>Especialidade técnica</span>
              </div>
              <div className="bsf-chip-section-chips">
                {ESP_FILTER_DEFS.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    chip={chip}
                    isActive={activeFilterKey === chip.key}
                    chipRefs={chipRefs}
                    onSelectFilter={handleSelectFilter}
                    count={(filters[STATE_KEYS[chip.key]] || []).length}
                  />
                ))}
              </div>
            </div>
            <label className="eq2-filter-inactive-toggle">
              <input
                type="checkbox"
                checked={Boolean(filters.showInactive)}
                onChange={(e) => onChange({ ...filters, showInactive: e.target.checked })}
              />
              <span>Exibir técnicos inativos</span>
            </label>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );

  return (
    <>
      <EcClickUpFilterChip
        anchorRef={anchorRef}
        label="Filtros técnicos"
        value={activeCount > 0 ? String(activeCount) : ''}
        icon={IconStack2}
        hasSelection={activeCount > 0}
        isOpen={menuOpen}
        ariaLabel="Abrir filtros técnicos"
        onClick={() => {
          setMenuOpen((o) => !o);
          setActiveFilterKey(null);
        }}
        showValue={activeCount > 0}
        className="ec-tech-filters-chip"
      />
      {floating}
      {activeDef && STATIC_KEYS.has(activeDef.key) ? (
        <StaticFilterPopover
          fieldKey={activeDef.key}
          listAriaLabel={activeDef.listAriaLabel}
          selected={filters[STATE_KEYS[activeDef.key]] || []}
          anchorRect={anchorRect}
          items={staticItemsByKey[activeDef.key] || []}
          onToggle={(value) => handleToggle(activeDef.key, value)}
          onToggleAll={(keys, allMarked) => handleToggleAll(activeDef.key, keys, allMarked)}
        />
      ) : null}
      {activeDef && !STATIC_KEYS.has(activeDef.key) ? (
        <AsyncFilterPopover
          fieldKey={activeDef.key}
          listAriaLabel={activeDef.listAriaLabel}
          selected={filters[STATE_KEYS[activeDef.key]] || []}
          anchorRect={anchorRect}
          onToggle={(value) => handleToggle(activeDef.key, value)}
          onToggleAll={(keys, allMarked) => handleToggleAll(activeDef.key, keys, allMarked)}
          onFetchSuggestions={onFetchSuggestions}
        />
      ) : null}
    </>
  );
}

export default Equipe2TechFilters;
